import React from "react";
import { Alert, Button, LinkExternal01 } from "@opendate-app/setlist";

import { useMarketingSettingsContext } from "../MarketingSettingsIndexContainer";
import DnsRecordsTable from "./DnsRecordsTable";

const DELIVERABILITY_ARTICLE_PATH = "/user_manual#marketing/email-deliverability/authenticating-your-sending-domain";

const RECORD_PURPOSES = {
  mail_cname: "SPF",
  subdomain_spf: "SPF",
  mail_server: "Return path",
  dkim: "DKIM",
  dkim1: "DKIM",
  dkim2: "DKIM",
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  copy: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    fontSize: 14,
    lineHeight: 1.5,
    color: "var(--sl-text-tertiary)",
  },
  heading: {
    fontSize: 14,
    fontWeight: 600,
    color: "var(--sl-text-primary)",
  },
  strong: {
    color: "var(--sl-text-secondary)",
  },
};

const dnsRecordRows = (sendDomain) => [
  ...(sendDomain.send_domain_dns_records || []).map((dnsRecord) => ({
    key: dnsRecord.id,
    purpose: RECORD_PURPOSES[dnsRecord.record_name] || dnsRecord.record_name,
    type: dnsRecord.record_type,
    host: dnsRecord.host,
    data: dnsRecord.data,
    verified: dnsRecord.verified,
  })),
  {
    key: "dmarc",
    purpose: "DMARC",
    type: "TXT",
    host: sendDomain.dmarc_host,
    data: sendDomain.dmarc_verified ? "Already published, no change needed" : sendDomain.recommended_dmarc_record,
    verified: !!sendDomain.dmarc_verified,
    copyable: !sendDomain.dmarc_verified,
  },
];

const SetupInstructions = ({
  sendDomain,
}) => {
  const { helpCenterEnabled } = useMarketingSettingsContext();

  return (
    <div style={styles.container}>
      {!sendDomain.verified ? (
        <Alert color="warning" title="Verification required" description="Add the DNS records below, then verify the domain." />
      ) : null}
      <div style={styles.copy}>
        <div style={styles.heading}>Configuration</div>
        <div>Opendate only sends email from domains that pass DKIM and SPF checks. These records prove to inboxes like Gmail and Outlook that Opendate is allowed to send email for your domain.</div>
        <div>Log in to wherever you manage your domain's DNS (e.g., GoDaddy, Namecheap, Cloudflare) and add each record below exactly as shown.</div>
        <div>
          <b style={styles.strong}>SPF:</b> The SPF record is set on a subdomain that Opendate sends from, so you don't need to change the SPF record on your main domain.
        </div>
        <div>
          <b style={styles.strong}>DMARC:</b> Gmail and Yahoo require a DMARC record for bulk email. A domain can only have one DMARC record. If you already have one, keep it and add the <code>rua=</code> tag shown below to it so we can help monitor your deliverability.
        </div>
      </div>
      <DnsRecordsTable records={dnsRecordRows(sendDomain)} />
      <div style={styles.copy}>
        <div style={styles.heading}>Verification</div>
        <div>DNS changes can take some time to propagate. It may take anywhere from a few minutes to 48 hours.</div>
        <div>Once the records have propagated, complete the verification process by clicking the "Verify domain" button above. You can't send email from this domain until it's verified.</div>
        {helpCenterEnabled ? (
          <div>
            <Button
              as="a"
              variant="link-color"
              trailingIcon={LinkExternal01}
              href={DELIVERABILITY_ARTICLE_PATH}
              target="_blank"
              rel="noopener noreferrer"
            >
              Learn more about setting up your sending domain
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default SetupInstructions;
